// Ocorrências pedagógicas — painel reutilizável (ficha do aluno e página /gestao/ocorrencias).
// Histórico interno; sigilosas só aparecem para quem pode editar o pedagógico.
import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  AlertTriangle, CalendarDays, Clock, EyeOff, Heart, Loader2, MessageSquare, Pencil,
  Plus, Star, Trash2, X, ShieldAlert,
} from 'lucide-react';
import api from '@/lib/api';
import { usePermV2 } from '@/lib/perm_v2';

export const TIPOS = [
  { v: 'indisciplina', label: 'Indisciplina', icon: AlertTriangle, cls: 'bg-rose-50 text-rose-700' },
  { v: 'saude', label: 'Saúde', icon: Heart, cls: 'bg-sky-50 text-sky-700' },
  { v: 'atraso', label: 'Atraso', icon: Clock, cls: 'bg-amber-50 text-amber-700' },
  { v: 'elogio', label: 'Elogio', icon: Star, cls: 'bg-emerald-50 text-emerald-700' },
  { v: 'comunicacao', label: 'Comunicação', icon: MessageSquare, cls: 'bg-indigo-50 text-indigo-700' },
  { v: 'pedagogico', label: 'Pedagógico', icon: ShieldAlert, cls: 'bg-violet-50 text-violet-700' },
];
export const GRAVIDADES = [
  { v: 'leve', label: 'Leve', cls: 'bg-slate-100 text-slate-600' },
  { v: 'media', label: 'Média', cls: 'bg-amber-100 text-amber-700' },
  { v: 'grave', label: 'Grave', cls: 'bg-rose-100 text-rose-700' },
];

const tipoInfo = (v) => TIPOS.find((t) => t.v === v) || TIPOS[5];
const gravInfo = (v) => GRAVIDADES.find((g) => g.v === v) || GRAVIDADES[0];
const dataBR = (d) => (d || '').split('-').reverse().join('/');

export function OcorrenciaForm({ value, onChange, onSubmit, onCancel, saving }) {
  const f = value || {};
  return (
    <div className="space-y-4" data-testid="ocorrencia-form">
      <div className="grid gap-3 sm:grid-cols-3">
        <label className="sm:col-span-1">
          <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-slate-400">Tipo</span>
          <select value={f.tipo || 'pedagogico'} onChange={(e) => onChange({ ...f, tipo: e.target.value })}
            className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-violet-300 focus:ring-4 focus:ring-violet-100"
            data-testid="ocorrencia-tipo">
            {TIPOS.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
          </select>
        </label>
        <label>
          <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-slate-400">Gravidade</span>
          <select value={f.gravidade || 'leve'} onChange={(e) => onChange({ ...f, gravidade: e.target.value })}
            className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-violet-300 focus:ring-4 focus:ring-violet-100"
            data-testid="ocorrencia-gravidade">
            {GRAVIDADES.map((g) => <option key={g.v} value={g.v}>{g.label}</option>)}
          </select>
        </label>
        <label>
          <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-slate-400">Data</span>
          <input type="date" value={f.data || ''} onChange={(e) => onChange({ ...f, data: e.target.value })}
            className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-violet-300 focus:ring-4 focus:ring-violet-100"
            data-testid="ocorrencia-data" />
        </label>
      </div>
      <label className="block">
        <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-slate-400">Título</span>
        <input value={f.titulo || ''} onChange={(e) => onChange({ ...f, titulo: e.target.value })}
          placeholder="Resumo em uma linha"
          className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm outline-none focus:border-violet-300 focus:ring-4 focus:ring-violet-100"
          data-testid="ocorrencia-titulo" />
      </label>
      <label className="block">
        <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-slate-400">Descrição</span>
        <textarea rows={4} value={f.descricao || ''} onChange={(e) => onChange({ ...f, descricao: e.target.value })}
          placeholder="O que aconteceu, quem estava presente, contexto..."
          className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm outline-none focus:border-violet-300 focus:ring-4 focus:ring-violet-100"
          data-testid="ocorrencia-descricao" />
      </label>
      <label className="block">
        <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-slate-400">Providência tomada</span>
        <input value={f.providencia || ''} onChange={(e) => onChange({ ...f, providencia: e.target.value })}
          placeholder="Conversa com a família, advertência, encaminhamento..."
          className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm outline-none focus:border-violet-300 focus:ring-4 focus:ring-violet-100"
          data-testid="ocorrencia-providencia" />
      </label>
      <div className="flex flex-wrap gap-5">
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input type="checkbox" checked={!!f.sigilosa} onChange={(e) => onChange({ ...f, sigilosa: e.target.checked })}
            data-testid="ocorrencia-sigilosa" />
          Sigilosa (só coordenação/direção)
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input type="checkbox" checked={!!f.visivel_familia} onChange={(e) => onChange({ ...f, visivel_familia: e.target.checked })}
            data-testid="ocorrencia-visivel-familia" />
          Visível à família
        </label>
      </div>
      <div className="flex justify-end gap-3">
        {onCancel && (
          <button onClick={onCancel} className="rounded-full border border-slate-200 px-5 py-2.5 text-sm font-bold text-slate-600 hover:bg-slate-50"
            data-testid="ocorrencia-cancelar">Cancelar</button>
        )}
        <button onClick={onSubmit} disabled={saving}
          className="inline-flex items-center gap-2 rounded-full bg-violet-600 px-6 py-2.5 text-sm font-bold text-white shadow-lg shadow-violet-600/20 transition-all hover:bg-violet-500 disabled:opacity-50"
          data-testid="ocorrencia-salvar">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Salvar ocorrência
        </button>
      </div>
    </div>
  );
}

export function OcorrenciaItem({ o, podeEditar, onEdit, onDelete, showStudent }) {
  const t = tipoInfo(o.tipo);
  const g = gravInfo(o.gravidade);
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border border-slate-200/80 bg-white p-4 transition-all hover:border-violet-200"
      data-testid={`ocorrencia-item-${o.id}`}>
      <div className="flex items-start gap-3">
        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${t.cls}`}>
          <t.icon className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="text-sm font-bold text-slate-900">{o.titulo}</h4>
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${g.cls}`}>{g.label}</span>
            {o.sigilosa && (
              <span className="inline-flex items-center gap-1 rounded-full bg-slate-900 px-2 py-0.5 text-[10px] font-bold uppercase text-white">
                <EyeOff className="h-3 w-3" /> sigilosa
              </span>
            )}
            {o.visivel_familia && (
              <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-700">família vê</span>
            )}
          </div>
          {showStudent && o.student_name && (
            <p className="mt-0.5 text-xs font-semibold text-violet-700">{o.student_name}</p>
          )}
          {o.descricao && <p className="mt-1.5 whitespace-pre-line text-sm leading-relaxed text-slate-600">{o.descricao}</p>}
          {o.providencia && (
            <p className="mt-2 rounded-xl bg-slate-50 px-3 py-2 text-xs text-slate-600">
              <strong className="font-bold text-slate-700">Providência:</strong> {o.providencia}
            </p>
          )}
          <div className="mt-2 flex items-center gap-3 text-xs text-slate-400">
            <span className="inline-flex items-center gap-1"><CalendarDays className="h-3 w-3" /> {dataBR(o.data)}</span>
            <span>{t.label}</span>
            {o.author_name && <span>por {o.author_name}</span>}
          </div>
        </div>
        {podeEditar && (
          <div className="flex shrink-0 gap-1">
            <button onClick={() => onEdit(o)} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-violet-700"
              data-testid={`ocorrencia-editar-${o.id}`}><Pencil className="h-3.5 w-3.5" /></button>
            <button onClick={() => onDelete(o)} className="rounded-lg p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600"
              data-testid={`ocorrencia-excluir-${o.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        )}
      </div>
    </motion.div>
  );
}

// Bloco de histórico + registro usado na ficha do aluno
export default function OcorrenciasPanel({ studentId, compact = false }) {
  const { can, perms } = usePermV2();
  const isAdmin = perms?.is_admin_bypass === true;
  const podeCriar = isAdmin || can('pedagogico', 'criar');
  const podeEditar = isAdmin || can('pedagogico', 'editar');

  const [items, setItems] = useState([]);
  const [resumo, setResumo] = useState({});
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const { data } = await api.get('/pedagogico/ocorrencias', { params: { student_id: studentId } });
      setItems(data.items || []);
      setResumo(data.resumo || {});
    } catch (e) {
      if (e?.response?.status !== 403) toast.error('Erro ao carregar ocorrências');
      setItems([]);
    } finally { setLoading(false); }
  }, [studentId]);

  useEffect(() => { load(); }, [load]);

  const salvar = async () => {
    if (!(form?.titulo || '').trim()) { toast.error('Informe o título da ocorrência.'); return; }
    setSaving(true);
    try {
      if (form.id) await api.put(`/pedagogico/ocorrencias/${form.id}`, form);
      else await api.post('/pedagogico/ocorrencias', { ...form, student_id: studentId });
      toast.success(form.id ? 'Ocorrência atualizada' : 'Ocorrência registrada');
      setForm(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao salvar ocorrência');
    } finally { setSaving(false); }
  };

  const excluir = async (o) => {
    try {
      await api.delete(`/pedagogico/ocorrencias/${o.id}`);
      toast.success('Ocorrência excluída');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro ao excluir'); }
  };

  return (
    <div className="space-y-4" data-testid="ocorrencias-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-400">
          Histórico de ocorrências
          {!loading && (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-600"
              data-testid="ocorrencias-total">{items.length}</span>
          )}
          {(resumo.grave || 0) > 0 && (
            <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] text-rose-700">{resumo.grave} grave(s)</span>
          )}
        </div>
        {podeCriar && !form && (
          <button onClick={() => setForm({ tipo: 'pedagogico', gravidade: 'leve', data: new Date().toISOString().slice(0, 10) })}
            className="inline-flex items-center gap-1.5 rounded-full bg-violet-600 px-4 py-2 text-xs font-bold text-white transition-all hover:bg-violet-500"
            data-testid="ocorrencia-nova">
            <Plus className="h-3.5 w-3.5" /> Registrar ocorrência
          </button>
        )}
      </div>

      <AnimatePresence>
        {form && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden rounded-2xl border border-violet-200 bg-violet-50/40 p-4">
            <div className="mb-3 flex items-center justify-between">
              <h4 className="text-sm font-bold text-slate-800">{form.id ? 'Editar ocorrência' : 'Nova ocorrência'}</h4>
              <button onClick={() => setForm(null)} className="rounded-lg p-1 text-slate-400 hover:bg-white">
                <X className="h-4 w-4" />
              </button>
            </div>
            <OcorrenciaForm value={form} onChange={setForm} onSubmit={salvar} onCancel={() => setForm(null)} saving={saving} />
          </motion.div>
        )}
      </AnimatePresence>

      {loading ? (
        <div className="flex items-center gap-2 py-4 text-sm text-slate-400">
          <Loader2 className="h-4 w-4 animate-spin" /> Carregando...
        </div>
      ) : items.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-slate-200 py-8 text-center text-sm text-slate-400"
          data-testid="ocorrencias-vazio">Nenhuma ocorrência registrada para este aluno.</p>
      ) : (
        <div className={`space-y-3 ${compact ? 'max-h-[420px] overflow-auto pr-1' : ''}`}>
          {items.map((o) => (
            <OcorrenciaItem key={o.id} o={o} podeEditar={podeEditar} onEdit={setForm} onDelete={excluir} />
          ))}
        </div>
      )}
    </div>
  );
}
